import React, { useState, useEffect } from "react";
import useTheme from "hooks/observer";
import VisualizationRenderers from "./VisualizationRenderers";
import "./ChartDashboard.scss";

// Simple Modal Component
const ReusableModal = ({ children, handleCancel, isOpen }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      style={{ 
        position: "fixed", 
        inset: 0, 
        background: "rgba(0,0,0,0.6)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        zIndex: 9999,
        padding: "20px"
      }}
      onClick={handleCancel}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw",
          maxHeight: "90vh",
          overflow: "auto"
        }}
      >
        {children}
      </div>
    </div>
  );
};

// Sample data generator function
const generateSampleData = (chart, isLineChart, isHistogram, isBarChart, isScatterPlot) => {
  if (isHistogram) {
    return Array.from({ length: 100 }, () => ({
      value: Math.random() * 3500
    }));
  }
  
  if (isLineChart) {
    return Array.from({ length: 20 }, (_, i) => ({
      x: i,
      y: Math.random() * 100
    }));
  }
  
  if (isScatterPlot) {
    return Array.from({ length: 50 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100
    }));
  }
  
  if (isBarChart) {
    return Array.from({ length: 10 }, (_, i) => ({
      category: `Item ${i + 1}`,
      value: Math.random() * 100
    }));
  }
  
  return [];
};

// Validation function
const validateAndProcessData = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  return data;
};

// Chart spec creator function
const createChartSpec = (chart, isModal = false, data = [], theme = "light") => {
  if (!chart || !chart.spec) return null;

  const isLineChart = chart.spec?.mark === "line" || chart.spec?.mark?.type === "line";
  const isHistogram = chart.spec?.mark === "bar" && chart.spec?.encoding?.x?.bin;
  const isBarChart = chart.spec?.mark === "bar" && !isHistogram;
  const isScatterPlot = chart.spec?.mark === "point" || chart.spec?.mark === "circle" || 
                       chart.spec?.mark?.type === "point" || chart.spec?.mark?.type === "circle";

  const lightThemeColors = [
    "#7c3aed", "#8b5cf6", "#a78bfa", "#c4b5fd", "#6366f1", "#ec4899",
    "#10b981", "#f59e0b", "#ef4444", "#3b82f6"
  ];

  const darkThemeColors = [
    "#a78bfa", "#c4b5fd", "#ddd6fe", "#8b5cf6", "#818cf8", "#f472b6",
    "#34d399", "#fbbf24", "#f87171", "#60a5fa"
  ];

  const colors = theme === "dark" ? darkThemeColors : lightThemeColors;
  const sizeFactor = isModal ? 1.5 : 1;
  
  const compactSpec = {
    ...chart.spec,
    width: isModal ? 900 : 380,
    height: isModal ? 500 : 240,
    autosize: {
      type: "fit",
      contains: "padding",
      resize: true
    },
    
    mark: isLineChart
      ? {
          type: "line",
          strokeWidth: 2.5 * sizeFactor,
          point: { 
            filled: true, 
            size: 50 * sizeFactor,
            opacity: 0.9,
            stroke: theme === "dark" ? "#ffffff" : "#000000",
            strokeWidth: 1
          },
          tooltip: true,
          interpolate: "monotone"
        }
      : isScatterPlot
      ? {
          type: "circle",
          size: 60 * sizeFactor,
          opacity: 0.85,
          strokeWidth: 1.5,
          stroke: theme === "dark" ? "#ffffff" : "#000000",
          tooltip: true
        }
      : isBarChart || isHistogram
      ? {
          type: "bar",
          cornerRadiusTopLeft: 4,
          cornerRadiusTopRight: 4,
          opacity: 0.9,
          stroke: isHistogram ? (theme === "dark" ? "#374151" : "#e5e7eb") : "transparent",
          strokeWidth: isHistogram ? 1 : 0,
          tooltip: true
        }
      : chart.spec.mark,

    config: {
      ...(chart.spec.config || {}),
      background: "transparent",
      view: { 
        stroke: "transparent",
        strokeWidth: 0,
        fill: "transparent"
      },
      axis: {
        labelFontSize: isModal ? 13 : 9,
        titleFontSize: isModal ? 15 : 10,
        labelColor: theme === "dark" ? "#d1d5db" : "#374151",
        titleColor: theme === "dark" ? "#f9fafb" : "#111827",
        gridColor: theme === "dark" ? "#374151" : "#e5e7eb",
        gridOpacity: theme === "dark" ? 0.3 : 0.5,
        domainColor: theme === "dark" ? "#4b5563" : "#9ca3af",
        tickColor: theme === "dark" ? "#4b5563" : "#9ca3af",
        labelPadding: 5,
        titlePadding: 8,
        titleFontWeight: 600,
        domain: false,
      },
      legend: {
        labelFontSize: isModal ? 13 : 9,
        titleFontSize: isModal ? 14 : 10,
        orient: isModal ? "right" : "bottom",
        direction: isModal ? "vertical" : "horizontal",
        labelColor: theme === "dark" ? "#d1d5db" : "#374151",
        titleColor: theme === "dark" ? "#f9fafb" : "#111827",
        padding: isModal ? 12 : 6,
        symbolSize: isModal ? 100 : 70,
        labelLimit: 120,
        rowPadding: 4,
        fillColor: "transparent",
        strokeColor: "transparent"
      },
      range: { 
        category: colors,
        ramp: colors,
        diverging: colors,
        heatmap: colors
      },
      mark: { 
        tooltip: true,
        fillOpacity: 0.85
      },
      style: {
        "guide-label": {
          fill: theme === "dark" ? "#d1d5db" : "#374151"
        },
        "guide-title": {
          fill: theme === "dark" ? "#f9fafb" : "#111827"
        },
        "group-title": {
          fill: theme === "dark" ? "#f9fafb" : "#111827"
        },
        "group-subtitle": {
          fill: theme === "dark" ? "#d1d5db" : "#6b7280"
        }
      }
    },

    padding: isModal 
      ? { left: 20, right: 20, top: 15, bottom: 30 }
      : { left: 8, right: 8, top: 8, bottom: 8 },
  };

  const hasData = data && Array.isArray(data) && data.length > 0;
  
  if (!hasData) {
    const sampleData = generateSampleData(chart, isLineChart, isHistogram, isBarChart, isScatterPlot);
    compactSpec.data = { values: sampleData };
  }

  if (compactSpec.encoding) {
    if (isHistogram) {
      const xField = compactSpec.encoding.x?.field || "value";
      
      delete compactSpec.encoding.color;
      
      compactSpec.encoding.x = {
        field: xField,
        type: "quantitative",
        bin: { 
          maxbins: 12,
          extent: [0, 3500]
        },
        title: compactSpec.encoding.x?.title || "Serve Time (ms)",
        axis: {
          labelAngle: -45,
          labelFontSize: isModal ? 13 : 10,
          titleFontSize: isModal ? 15 : 11,
          labelLimit: isModal ? 200 : 100,
          format: ".0f",
          tickCount: 12,
          labelOverlap: false,
          tickMinStep: 200
        },
        scale: {
          zero: true,
          domain: [0, 3500],
          nice: false
        }
      };
      
      compactSpec.encoding.y = {
        aggregate: "count",
        type: "quantitative",
        title: "Count",
        axis: {
          labelFontSize: isModal ? 13 : 10,
          titleFontSize: isModal ? 15 : 11,
          tickCount: isModal ? 6 : 5,
          grid: true,
          format: ".0f"
        },
        scale: {
          nice: true,
          zero: true
        }
      };
      
      compactSpec.mark = {
        type: "bar",
        cornerRadiusTopLeft: 3,
        cornerRadiusTopRight: 3,
        opacity: 0.9,
        tooltip: true,
        binSpacing: 3,
        color: colors[0]
      };
      
      compactSpec.encoding.tooltip = [
        { field: xField, type: "quantitative", bin: true, title: "Serve Time Range (ms)" },
        { aggregate: "count", type: "quantitative", title: "Count", format: ",.0f" }
      ];
    } else {
      if (compactSpec.encoding.x) {
        compactSpec.encoding.x = {
          ...compactSpec.encoding.x,
          axis: {
            ...(compactSpec.encoding.x.axis || {}),
            labelAngle: compactSpec.encoding.x.type === "nominal" ? -40 : 0,
            labelFontSize: isModal ? 13 : 9,
            labelLimit: isModal ? 200 : 80,
            labelOverlap: "parity",
            tickCount: isModal ? 8 : 5,
          },
          scale: {
            ...(compactSpec.encoding.x.scale || {}),
            nice: true,
            padding: 0.1,
          },
        };
      }

      if (compactSpec.encoding.y) {
        compactSpec.encoding.y = {
          ...compactSpec.encoding.y,
          axis: {
            ...(compactSpec.encoding.y.axis || {}),
            labelFontSize: isModal ? 13 : 9,
            tickCount: isModal ? 6 : 5,
            grid: true,
            format: compactSpec.encoding.y.type === "quantitative" ? "~s" : undefined,
          },
          scale: {
            ...(compactSpec.encoding.y.scale || {}),
            nice: true,
            zero: isBarChart,
          },
        };
      }

      if (!compactSpec.encoding.tooltip) {
        compactSpec.encoding.tooltip = [
          ...(compactSpec.encoding.x ? [{
            field: compactSpec.encoding.x.field,
            type: compactSpec.encoding.x.type,
            title: compactSpec.encoding.x.title || compactSpec.encoding.x.field
          }] : []),
          ...(compactSpec.encoding.y ? [{
            field: compactSpec.encoding.y.field,
            type: compactSpec.encoding.y.type,
            title: compactSpec.encoding.y.title || compactSpec.encoding.y.field,
            format: compactSpec.encoding.y.type === "quantitative" ? ",.2f" : undefined
          }] : []),
        ];
      }
    }

    if (compactSpec.encoding.color) {
      compactSpec.encoding.color = {
        ...compactSpec.encoding.color,
        scale: { range: colors },
        legend: {
          ...(compactSpec.encoding.color.legend || {}),
          labelFontSize: isModal ? 13 : 9,
          symbolSize: isModal ? 100 : 70,
        },
      };
    }
  }

  return compactSpec;
};

// Main Component - Default Export
function ChartDashboard({
  suggestedCharts = [],
  selectedCharts = [],
  csvData = [],
  chartRefs,
  isHistoryOpen = true,
}) {
  const theme = useTheme();
  const chartsToRender = (Array.isArray(selectedCharts) && selectedCharts.length > 0)
    ? selectedCharts
    : suggestedCharts || [];

  const [expandedChart, setExpandedChart] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processedData, setProcessedData] = useState([]);

  useEffect(() => {
    const validatedData = validateAndProcessData(csvData);
    if (validatedData) {
      setProcessedData(validatedData);
    } else {
      setProcessedData([]);
    }
  }, [csvData]);

  const handleOpenChart = (chart) => {
    setExpandedChart(chart);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 300);
  };

  const handleCloseChart = () => setExpandedChart(null);

  const getGridColumns = () => {
    const count = chartsToRender.length;
    if (count === 1) return "1fr";
    if (count === 2) return "repeat(2, 1fr)";
    if (count === 3) return "repeat(3, 1fr)";
    if (count === 4) return "repeat(4, 1fr)";
    return `repeat(${Math.min(count, 4)}, minmax(350px, 1fr))`;
  };

  const handleDownloadChart = async (chart) => {
    try {
      const modalBody = document.querySelector(".chart-modal-body");
      if (!modalBody) {
        alert("Chart not found");
        return;
      }

      const svgElement = modalBody.querySelector("svg");
      const canvasElement = modalBody.querySelector("canvas");

      if (svgElement) {
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const downloadLink = document.createElement("a");
        downloadLink.href = svgUrl;
        downloadLink.download = `${chart.name.replace(/[^a-z0-9]/gi, "_") || "chart"}.svg`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(svgUrl);
      } else if (canvasElement) {
        canvasElement.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const downloadLink = document.createElement("a");
          downloadLink.href = url;
          downloadLink.download = `${chart.name.replace(/[^a-z0-9]/gi, "_") || "chart"}.png`;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);
        });
      } else {
        alert("Could not find chart to download");
      }
    } catch (error) {
      console.error("Error downloading chart:", error);
      alert("Failed to download chart. Please try again.");
    }
  };

  return (
    <div className={`chart-dashboard ${isHistoryOpen ? "sidebar-open" : ""}`}>
      <main className="charts-center">
        {chartsToRender.length === 0 ? (
          <div className="charts-empty-state">
            <div className="empty-state-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <rect x="12" y="30" width="15" height="35" rx="3" fill="#7c3aed" opacity="0.7"/>
                <rect x="32" y="20" width="15" height="45" rx="3" fill="#8b5cf6" opacity="0.8"/>
                <rect x="52" y="25" width="15" height="40" rx="3" fill="#a78bfa" opacity="0.9"/>
              </svg>
            </div>
            <h3>No Charts Available</h3>
            <p>Select or create charts from the suggestions panel</p>
          </div>
        ) : (
          <div 
            className="charts-grid"
            style={{
              gridTemplateColumns: getGridColumns(),
            }}
          >
            {chartsToRender.map((chart, index) => {
              const chartSpec = createChartSpec(chart, false, processedData, theme);
              const recordCount = processedData.length || csvData.length || 1866;
              
              return (
                <div
                  key={chart.id || index}
                  ref={(el) => {
                    if (chartRefs?.current) {
                      chartRefs.current[chart.id || index] = el;
                    }
                  }}
                  className="chart-card"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => handleOpenChart(chart)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenChart(chart);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${chart.name} in full screen`}
                >
                  <div className="chart-header">
                    <div className="chart-title">
                      <h3 title={chart.name}>{chart.name}</h3>
                      <span className="chart-type-badge">
                        {chart.spec?.mark?.type || chart.spec?.mark || "CHART"}
                      </span>
                    </div>
                    <div className="chart-actions">
                      <button 
                        className="expand-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenChart(chart);
                        }}
                        aria-label="Expand chart"
                      >
                        ↗
                      </button>
                    </div>
                  </div>

                  <div className="chart-body">
                    {chartSpec ? (
                      <VisualizationRenderers 
                        spec={chartSpec} 
                        data={processedData.length > 0 ? processedData : undefined} 
                      />
                    ) : (
                      <div className="chart-error">
                        <div className="error-icon">⚠️</div>
                        <p>Unable to render chart</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="chart-footer">
                    <span className="chart-stats">
                      {recordCount} records
                    </span>
                    <button 
                      className="chart-hint-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenChart(chart);
                      }}
                      aria-label="Expand chart"
                    >
                      Click to expand
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {expandedChart && (
        <ReusableModal 
          handleCancel={handleCloseChart}
          width="90vw"
          height="90vh"
          isOpen={!!expandedChart}
        >
          <div className="chart-modal">
            <div className="chart-modal-header">
              <div>
                <h2>{expandedChart.name}</h2>
                <p className="modal-subtitle">
                  Full screen visualization • {processedData.length || csvData.length || 1866} data points
                </p>
              </div>
              <button 
                className="modal-close-btn"
                onClick={handleCloseChart}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <div className="chart-modal-body">
              {isLoading ? (
                <div className="chart-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading chart...</p>
                </div>
              ) : (
                <VisualizationRenderers 
                  spec={createChartSpec(expandedChart, true, processedData, theme)} 
                  data={processedData.length > 0 ? processedData : undefined} 
                />
              )}
            </div>
            
            <div className="chart-modal-footer">
              <div className="modal-actions">
                <button 
                  className="btn-primary btn-download-chart"
                  onClick={() => handleDownloadChart(expandedChart)}
                >
                  📥 Download Chart
                </button>
              </div>
            </div>
          </div>
        </ReusableModal>
      )}
    </div>
  );
}

// Export both the component and the utility function
export { createChartSpec };
export default ChartDashboard;